import { EXTRA_QUESTS } from './quests_extra.js';
// Quests: requisitos, quantos jogadores (alavanca no estilo da Annihilator) e as cenas da história.
// Cada personagem conclui cada quest uma única vez.

export interface CutsceneSlide { title?: string; text: string; tint: string }

export interface QuestDef {
  id: string; name: string; map: string;
  level: number;
  /** promotion mínima para entrar (e a que bloqueia: quem já tem não precisa repetir) */
  promotion?: number;
  /** jogadores nos ladrilhos da alavanca: mínimo (quests de grupo exigem o grupo completo) e máximo */
  minPlayers: number; maxPlayers: number;
  intro: CutsceneSlide[]; boss: CutsceneSlide[]; outro: CutsceneSlide[];
  /** quest clássica do Tibia (entra pela janela de Quests; conclui quando todos os monstros caem) */
  classic?: boolean;
  /** quest que precisa ter sido concluída antes (ex.: Scrapper exige Greenhorn) */
  requires?: string;
  /** texto curto da janela de Quests */
  desc?: string;
  /** recompensas: escolha uma (baús que dividem o mesmo storage no Tibia) e as que vêm todas; gold vai para o banco */
  choose?: Array<[number, number]>; all?: Array<[number, number]>; gold?: number;
  /** o prêmio de `choose` é sorteado (como no Global), em vez de escolhido */
  random?: boolean;
}

const classic = (id: string, name: string, level: number, maxPlayers: number, desc: string, r: Pick<QuestDef, 'choose' | 'all' | 'gold' | 'requires' | 'random'>, tint = '#140a0a'): QuestDef => ({
  id, name, map: 'cq-' + id, level, minPlayers: 1, maxPlayers, classic: true, desc, ...r,
  intro: [{ title: name, tint, text: desc }], boss: [],
  outro: [{ title: 'Quest concluída', tint: '#1a1608', text: r.choose ? 'Os baús estão abertos. Escolha sua recompensa na janela de Quests.' : 'Os baús estão abertos e a recompensa já é sua.' }],
});

/** Quests clássicas do Tibia: recompensas oficiais (baús do Canary, data/startup/tables/chest.lua e scripts das quests). */
export const CLASSIC_QUESTS: QuestDef[] = ([
  classic('banshee', 'The Queen of the Banshees', 60, 4,
    'Atravesse os selos da Rainha das Banshees: fantasmas, vampiros e liches guardam cada selo. No fim, só um dos baús pode ser aberto.',
    { choose: [[3081, 1], [3049, 1], [3428, 1], [3281, 1], [3079, 1]], gold: 10_000 }, '#0a0a18'),
  classic('beregar', 'The Hidden City of Beregar', 60, 4,
    'Desça às minas perdidas dos anões de Beregar, tomadas pelos Lost e pelos anões escravizados, e recupere o tesouro do ferreiro.',
    { all: [[9019, 1], [9058, 1]] }, '#18100a'),
  classic('medusa', 'The Medusa Quest', 70, 4,
    'No templo afogado, as serpentes guardam o covil da Medusa. Derrote-a e leve tudo o que ela escondia.',
    { all: [[3436, 1], [3567, 1], [3324, 1]] }, '#0a1810'),
  classic('demonhelmet', 'Demon Helmet Quest', 80, 4,
    'Pelas Tumbas Antigas, entre escaravelhos, múmias e liches, até o demônio que guarda o elmo. Os três baús são seus.',
    { all: [[3387, 1], [3554, 1], [3420, 1]] }, '#1a140a'),
  classic('pits', 'The Pits of Inferno', 80, 4,
    'Cruze a ponte levadiça, o labirinto de Bazir e o salão dos tronos dos Ruthless Seven. Escolha uma das armas lendárias e leve o resto do tesouro.',
    { choose: [[3341, 1], [6527, 1], [5803, 1]], all: [[6529, 1], [3249, 1]], gold: 10_000 }, '#2a0800'),
  classic('arena-greenhorn', 'Arena de Svargrond: Greenhorn', 30, 1,
    'Dez lutas seguidas na arena dos bárbaros, sozinho. Vença todas para escolher uma arma de campeão.',
    { choose: [[7392, 1], [7380, 1], [7406, 1]] }, '#0a1418'),
  classic('arena-scrapper', 'Arena de Svargrond: Scrapper', 60, 1,
    'A segunda dificuldade da arena: lutadores mais duros e os três espíritos dos elementos.',
    { choose: [[7415, 1], [7389, 1], [7384, 1]], requires: 'arena-greenhorn' }, '#0a1418'),
  classic('arena-warlord', 'Arena de Svargrond: Warlord', 90, 1,
    'A dificuldade máxima da arena. Só os maiores guerreiros de Svargrond chegam ao fim.',
    { choose: [[7429, 1], [7434, 1], [7390, 1]], requires: 'arena-scrapper' }, '#0a1418'),
  classic('annihilator', 'The Annihilator', 100, 4,
    'A alavanca mais famosa do Tibia: seis demônios entre você e os baús. Só um deles pode ser aberto.',
    { choose: [[3388, 1], [3288, 1], [3319, 1], [3213, 1]] }, '#2a0000'),
  classic('inquisition', 'The Inquisition', 120, 4,
    'A missão final da Inquisição: atravesse os seis wards dos demônios (Ushuriel, Zugurosh, Madareth, os irmãos Latrivan e Golgordan, Annihilon e Hellgorak) até o Nexus das Sombras.',
    { choose: [[8062, 1], [8090, 1], [8053, 1], [8060, 1], [8023, 1], [8096, 1], [8100, 1], [8102, 1], [8026, 1]] }, '#14001a'),
  classic('wrath', 'Wrath of the Emperor', 120, 4,
    'Invada a fortaleza de Zao, passe pelos dracônicos do Imperador e derrote a Essência do Deus Serpente.',
    { choose: [[50263, 1], [11687, 1], [11686, 1], [11689, 1]] }, '#1a0a00'),
  // quests clássicas com baús do Canary (data-otservbr-global/startup/tables/chest.lua): todos os baús são abertos
  classic('trollcave', 'Troll Cave Quest', 8, 4,
    'A caverna dos trolls esconde dois baús antigos. Atravesse os túneis e o covil do Troll Champion.',
    { all: [[3083, 1], [3372, 1]] }, '#10140a'),
  classic('barbarianaxe', 'Barbarian Axe Quest', 20, 4,
    'No labirinto dos minotauros, os baús guardam um Barbarian Axe e uma Scimitar.',
    { all: [[3317, 1], [3307, 1]] }, '#18100a'),
  classic('alawar', "Alawar's Vault", 25, 4,
    'Djinns verdes e azuis e os efreets e marids vigiam o cofre esquecido de Alawar.',
    { all: [[3026, 3], [3301, 1]] }, '#1a140a'),
  classic('darkarmor', 'Dark Armor Quest', 25, 4,
    'Caçadores, guerreiros selvagens e um herói guardam a Dark Armor no fundo da caverna.',
    { all: [[3383, 1]] }, '#0a0a0a'),
  classic('orcfortress', 'Orc Fortress Quest', 30, 4,
    'Invada a fortaleza dos orcs, passe pelos berserkers e pelos líderes e chegue ao tesouro do Orc Warlord.',
    { all: [[3318, 1], [3370, 1], [3280, 1]] }, '#14100a'),
  classic('ringquest', 'Ring Quest', 30, 4,
    'Golems de pedra e gárgulas protegem os dois anéis da galeria: Time Ring e Sword Ring.',
    { all: [[3053, 1], [3091, 1]] }, '#0a0a14'),
  classic('timering', 'Time Ring Quest', 30, 4,
    'Os bonelords vigiam a câmara do tempo. Lá dentro, uma Crystal Ball, um Time Ring e um Elven Amulet.',
    { all: [[3076, 1], [3053, 1], [3082, 1]] }, '#0a0a18'),
  classic('paradox', 'The Paradox Tower', 30, 4,
    'Suba a torre dos paradoxos entre warlocks e cientistas loucos. No topo, só um dos prêmios pode ser escolhido.',
    { choose: [[3215, 1], [3035, 100], [3034, 32], [3073, 1]] }, '#140a18'),
  classic('dragontower', 'Dragon Tower Quest', 35, 4,
    'Os dragões tomaram a torre. No topo ficam duas mochilas com poções, flechas e um arco.',
    { all: [[268, 1], [266, 1], [3449, 30], [3448, 60], [3350, 1], [3029, 2]] }, '#1a0800'),
  classic('madmage', 'Mad Mage Room', 40, 4,
    'O laboratório do Mago Louco: necromantes, warlocks e o próprio Mad Mage. Os três baús são seus.',
    { all: [[3014, 1], [3210, 1], [3081, 5]] }, '#100a18'),
  classic('vampireshield', 'Vampire Shield Quest', 40, 4,
    'Na cripta dos vampiros estão a Dragon Lance, o Vampire Shield e uma bolsa com símbolos estranhos.',
    { all: [[3302, 1], [3434, 1], [3058, 1], [3027, 1], [3078, 1]] }, '#18000a'),
  classic('blackknight', 'Black Knight Quest', 50, 4,
    'Atravesse a floresta morta e derrote os cavaleiros negros para levar a Crown Armor e o Crown Shield.',
    { all: [[3381, 1], [3419, 1]] }, '#0a0a0a'),
  classic('doublehero', 'Double Hero Quest', 50, 4,
    'Dois heróis e uma valquíria guardam a câmara: um Red Gem e um Club Ring esperam no fim.',
    { all: [[3039, 1], [3093, 1]] }, '#14100a'),
  classic('draconia', 'Draconia Quest', 50, 4,
    'No templo de Draconia, dragões e dragon lords cercam o altar. Ice Rapier, Serpent Sword, Stone Skin Amulets e um Energy Ring.',
    { all: [[3284, 1], [3297, 1], [3081, 5], [3051, 1]] }, '#1a0800'),
  classic('fireaxe', 'Fire Axe Quest', 60, 4,
    'Fire devils, elementais de fogo e um demônio guardam o Fire Axe e uma bolsa com joias.',
    { all: [[3320, 1], [3098, 1], [3085, 1], [3028, 7]] }, '#2a0800'),
  classic('behemoth', 'Behemoth Quest', 80, 4,
    'Nos túneis dos gigantes, os behemoths guardam Guardian Halberd, Demon Shield, Golden Armor e uma bolsa de joias.',
    { all: [[3315, 1], [3420, 1], [3360, 1], [3028, 3], [3029, 4], [3007, 1], [3052, 1], [3055, 1]] }, '#18100a'),
  ...EXTRA_QUESTS.map((q) => classic(q.id, q.name, q.level, q.maxPlayers, q.desc, q.r)),
]).sort((a, b) => a.level - b.level);

export const QUESTS: Record<string, QuestDef> = {
  ascension: {
    id: 'ascension', name: 'A Ascensão', map: 'quest-ascension', level: 400, promotion: 1, minPlayers: 1, maxPlayers: 4,
    intro: [
      { title: 'A Ascensão', tint: '#1a0f08',
        text: 'Há mil anos, Noxara quase caiu. Do fundo da terra subiu o Devorador, um ser que se alimentava da coragem dos vivos.' },
      { tint: '#140a14',
        text: 'Os primeiros heróis da Torre o enfrentaram. Não puderam destruí-lo. Usaram as próprias almas para selá-lo num santuário fora do mundo.' },
      { tint: '#0a0a18',
        text: 'O selo está rachando. Guardas demoníacos, mortos que não descansam e a própria escolta do Devorador já atravessaram as fendas.' },
      { tint: '#10140a',
        text: 'Só quem atravessar o santuário e derrubar o Avatar do Devorador terá o nome gravado ao lado dos primeiros heróis.' },
      { title: 'Entre no santuário', tint: '#000000',
        text: 'Limpe o Salão do Juramento, a Galeria dos Caídos e a Ponte do Abismo. Com as três salas purificadas, o Avatar desperta no trono.' },
    ],
    boss: [
      { title: 'O Avatar desperta', tint: '#2a0000',
        text: 'O chão do trono se abre. Uma voz que não é de ninguém enche o santuário: "Mais almas para o meu banquete."' },
    ],
    outro: [
      { title: 'O selo foi restaurado', tint: '#1a1608',
        text: 'O Avatar se desfaz em cinzas e o santuário fica em silêncio. Pela primeira vez em mil anos, o selo brilha inteiro.' },
      { title: 'Ascensão', tint: '#201a00',
        text: 'Uma luz dourada te envolve. Os primeiros heróis te reconhecem: sua vida e sua mana crescem, e suas habilidades ficam mais afiadas.' },
    ],
  },
};
for (const q of CLASSIC_QUESTS) QUESTS[q.id] = q;
